import './SearchPopup.scss'

import {Fetcher} from '@codeurs/front'
import queryString from 'query-string'
import React, {useEffect, useState} from 'react'
import {FormProvider, useForm} from 'react-hook-form'
import ReactPaginate from 'react-paginate'
import {useHistory} from 'react-router'
import {Theme} from 'views/Theme'
import {PaginationResult} from '../../../types'
import {useTranslation} from '../../../util/i18n'
import {style} from '../../../util/style'
import {
	FieldsubmitField,
	FieldsubmitRow,
	FieldsubmitSubmit,
	FieldsubmitType
} from '../../ui/Fieldsubmit'
import {Icon} from '../../ui/Icon'
import {Popup, PopupClose, PopupType} from '../../ui/Popup'
import {Input, Label} from '../Form'
import {SearchResult, SearchResultType} from './SearchResult'

const styles = style('searchpopup', ['top', 'results', 'pagination'])

export type SearchPopupType = PopupType & {results?: Array<SearchResultType>}

type searchType = {
	query: string
	page: number
}

export const SearchPopup: React.FC<SearchPopupType> = ({
	open,
	setOpen,
	onClose
}) => {
	const [search, setSearch] = useState<searchType>({query: '', page: 1})
	const history = useHistory()
	const t = useTranslation('search')

	const setUrlSearch = (T: searchType) => {
		setSearch(T)
		const {search: locSearch, ...locRest} = queryString.parse(location.search)
		history.push('?' + queryString.stringify({...locRest, search: T.query}))
	}

	useEffect(() => {
		if (open) return
		const {search: searchQuery} = queryString.parse(location.search)
		if (!searchQuery) return
		setOpen(true)
		setSearch({query: searchQuery, page: 1})
	}, [])

	const closePopup = () => {
		onClose()
		const {search: locSearch, ...locRest} = queryString.parse(location.search)
		history.push('?' + queryString.stringify({...locRest}))
		setTimeout(() => {
			setSearch({query: '', page: 1})
		}, 500)
	}

	const setPage = (p: number) => {
		setSearch((prevSearch) => ({...prevSearch, page: p}))
		document.getElementById('searchpopup').scrollIntoView()
	}

	return (
		<Popup open={open} onClose={closePopup} mod={'search'}>
			<div className={styles()} id={'searchpopup'}>
				<SearchPopupTop
					label={t.search}
					type="search"
					name="search"
					submit={t.submit}
					onClose={closePopup}
					setSearch={setUrlSearch}
				/>
				{search.query && search.query.length > 3 ? (
					<Fetcher<PaginationResult<SearchResultType>>
						url={'/api/search?q=' + search.query + '&page=' + search.page}
					>
						{({isLoading, data, error}) => {
							return (
								<>
									<SearchPopupResults
										isLoading={isLoading}
										result={data}
										term={search.query}
									/>
									{!isLoading && (
										<SearchPopupPagination result={data} setPage={setPage} />
									)}
								</>
							)
						}}
					</Fetcher>
				) : (
					<div className={styles('empty')}>{t.empty}</div>
				)}
			</div>
		</Popup>
	)
}

const SearchPopupTop: React.FC<
	FieldsubmitType & {
		onClose: () => void
		setSearch: (q: searchType) => void
	}
> = ({label, type, name, submit, onClose, setSearch}) => {
	const {search: locSearch} = queryString.parse(location.search)
	const [formValue, setFormValue] = useState(locSearch || '')
	const methods = useForm()
	const {handleSubmit} = methods

	const onSubmit = (data) => {
		if (formValue.length < 4) return
		setSearch({query: data.search, page: 1})
	}

	//If search on input is needed, uncomment:
	//let oninput: any = false

	return (
		<div className={styles.top()}>
			<FormProvider {...methods}>
				<form onSubmit={handleSubmit(onSubmit)} className={styles.top('form')}>
					<FieldsubmitRow>
						<FieldsubmitField>
							<Label htmlFor={name} label={label} />
							<Input
								type={type}
								name={name}
								required
								min={3}
								value={formValue}
								onChange={(e) => {
									setFormValue((e.target as HTMLInputElement).value)
									//If search on input is needed, uncomment:
									// clearTimeout(oninput)
									// oninput = setTimeout(() => {
									// 	if (!e.target.value) return
									// 	if (e.target.value.length < 4) return
									// 	setSearch({query: e.target.value, page: 1})
									// }, 500)
								}}
							/>
						</FieldsubmitField>
						<FieldsubmitSubmit>
							<Theme.Button
								as="button"
								mod="boldtext"
								disabled={formValue.length < 4}
							>
								{submit || 'Submit'}
							</Theme.Button>
						</FieldsubmitSubmit>
					</FieldsubmitRow>
				</form>
			</FormProvider>
			<PopupClose className={styles.top('close')} onClose={onClose} />
		</div>
	)
}

const SearchPopupResults: React.FC<{
	isLoading: boolean
	term: string
	result?: PaginationResult<SearchResultType>
}> = ({isLoading, result, term}) => {
	const t = useTranslation('search').results
	if (isLoading)
		return <div className={styles.results('loading')}>{t.loading}</div>
	if (!result || !result.total)
		return <div className={styles.results('empty')}>{t.empty}</div>
	return (
		<div className={styles.results()}>
			{result.data.map((searchResult) => (
				<div key={searchResult.url} className={styles.results('item')}>
					<SearchResult {...searchResult} term={term} />
				</div>
			))}
		</div>
	)
}

const SearchPopupPagination: React.FC<{
	result: PaginationResult<SearchResultType>
	setPage: (p: number) => void
}> = ({result, setPage}) => {
	if (!result || !result.total) return null
	const showArrows = result.per_page && result.per_page < result.total
	if (!showArrows) return null
	return (
		<div className={styles.pagination()}>
			<ReactPaginate
				previousLabel={
					<div className={styles.pagination('arrow').mod('prev')}>
						<span className={styles.pagination('arrow-icon')}>
							<Icon icon={'chevronRight'} />
						</span>
					</div>
				}
				nextLabel={
					<div className={styles.pagination('arrow')}>
						<span className={styles.pagination('arrow-icon')}>
							<Icon icon={'chevronRight'} />
						</span>
					</div>
				}
				forcePage={result.current_page - 1}
				pageCount={result.last_page}
				pageRangeDisplayed={3}
				marginPagesDisplayed={2}
				onPageChange={({selected}) => {
					setPage(selected + 1)
				}}
				containerClassName={styles.pagination('container')}
				pageClassName={styles.pagination('page')}
				previousClassName={styles.pagination('prev')}
				nextClassName={styles.pagination('next')}
				breakClassName={styles.pagination('break')}
				activeClassName="is-active"
			/>
		</div>
	)
}
